<template>
  <div :id="`order_kpi_date_change_${type}`">
    <b-modal :active.sync="dateChangeModal.active" width="960px" has-modal-card>
      <order-kpi-date-change-modal-request
        :order-id="order.id"
        :type="type"
        @submit="onDateChangeModalSubmit"
      />
    </b-modal>
    <b-modal
      :active.sync="dateChangeRejectModal.active"
      width="960px"
      has-modal-card
    >
      <order-kpi-date-change-modal-reject
        :order-id="order.id"
        :type="type"
        @submit="onDateChangeRejectModalSubmit"
      />
    </b-modal>
    <div class="columns is-vcentered is-variable is-1">
      <div class="column is-6">
        <div
          class="buttons"
          v-if="this.dateChangeStatus === this.$options.statuses.pending"
        >
          <b-button
            class="is-fullwidth kpi-btn"
            :loading="withdrawing"
            :disabled="(canWithdrawDateChange && !canWithdrawDateChange.value) || !order.editable"
            :title="
              !canWithdrawDateChange.value
                ? canWithdrawDateChange && canWithdrawDateChange.help[0]
                : ''
            "
            @click="withdrawDateChange"
            v-if="
              canWithdrawDateChangeByUser && canWithdrawDateChangeByUser.value
            "
          >
            <p class="kpi-btn-text">WITHDRAW</p>
          </b-button>
        </div>
        <div class="buttons" v-else>
          <b-button
            :id="`order_kpi_expected_${type}_change`"
            class="is-fullwidth has-text-weight-bold"
            :disabled="(canRequestDateChange && !canRequestDateChange.value) || !order.editable"
            :title="
              !canRequestDateChange.value
                ? canRequestDateChange && canRequestDateChange.help[0]
                : ''
            "
            @click="dateChangeModal.active = true"
            v-if="
              canRequestDateChangeByUser && canRequestDateChangeByUser.value
            "
          >
            CHANGE
          </b-button>
        </div>
      </div>
      <div class="column is-6">
        <div class="buttons" v-if="dateChangeStatus">
          <b-tooltip
            :id="`order_kpi_date_change_${type}_pending`"
            :label="hoverText"
            type="is-dark"
            multilined
            style="width:100%;height:100%"
            @mouseover.native="hovering = true"
            @mouseleave.native="hovering = false"
            v-if="
              dateChangeStatus === $options.statuses.pending &&
                (
                  (canApproveDateChangeByUser && canApproveDateChangeByUser.value)
                  ||
                  (canRejectDateChangeByUser && canRejectDateChangeByUser.value)
                )
              "
          >
            <b-field style="width:100%" v-if="hovering">
              <p class="control" style="width:50%">
                <b-button
                  :id="`order_kpi_expected_${type}_approve`"
                  class="is-fullwidth"
                  type="is-success"
                  icon-right="check-circle"
                  :disabled="(canApproveDateChange && !canApproveDateChange.value) || !order.editable"
                  :loading="approving"
                  @click="approveDateChange"
                  v-if="canApproveDateChangeByUser && canApproveDateChangeByUser.value"
                />
              </p>
              <p class="control" style="width:50%">
                <b-button
                  :id="`order_kpi_expected_${type}_reject`"
                  class="is-fullwidth"
                  type="is-danger"
                  icon-right="times-circle"
                  :disabled="(canRejectDateChange && !canRejectDateChange.value) || !order.editable"
                  @click="dateChangeRejectModal.active = true"
                  v-if="canRejectDateChangeByUser && canRejectDateChangeByUser.value"
                />
              </p>
            </b-field>
            <b-button
              id="date_change"
              :type="dateChangeStatus | dateChangeStatusType"
              :icon-left="hoverText ? 'info-circle' : ''"
              :disabled="!order.editable"
              class="is-fullwidth kpi-btn ws-normal"
              v-else
            >
              <p class="kpi-btn-text">
                {{ `Change ${dateChangeStatus}` | uppercase }}
              </p>
            </b-button>
          </b-tooltip>
          <b-tooltip
            :label="hoverText"
            type="is-dark"
            multilined
            style="width:100%"
            v-else
          >
            <b-button
              :type="dateChangeStatus | dateChangeStatusType"
              :icon-left="hoverText ? 'info-circle' : ''"
              :disabled="!order.editable"
              class="is-fullwidth kpi-btn ws-normal"
            >
              <p class="kpi-btn-text">
                {{ `Change ${dateChangeStatus}` | uppercase }}
              </p>
            </b-button>
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  
import OrderKpiDateChangeModalRequest from "./OrderKpiDateChangeModalRequest";
import OrderKpiDateChangeModalReject from "./OrderKpiDateChangeModalReject";
import { OrderService } from "@/services/order.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import { ConfigurationValidator } from "@/helpers/ConfigurationValidator";
import { DateFormat } from "@/helpers/date-format";
import { startCase } from "lodash";

export default {
  orderService: OrderService,
  statuses: {
    approved: "APPROVED",
    rejected: "REJECTED",
    withdrawn: "WITHDRAWN",
    pending: "PENDING"
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    type: {
      validator: value => {
        return ["start", "finish"].indexOf(value) !== -1;
      },
      required: true
    }
  },
  filters: {
    dateChangeStatusType(status) {
      switch (status) {
        case "APPROVED":
          return "is-success";
        case "REJECTED":
          return "is-danger";
        case "WITHDRAWN":
          return "is-info";
        case "PENDING":
          return "is-warning";
      }
    }
  },
  components: {
    OrderKpiDateChangeModalRequest,
    OrderKpiDateChangeModalReject
  },
  data: () => ({
    dateChangeModal: {
      active: false
    },
    dateChangeRejectModal: {
      active: false
    },
    withdrawn: false,
    approving: false,
    rejecting: false,
    withdrawing: false,
    hovering: false
  }),
  computed: {
    dateChangeLogs() {
      const logs = this.order.dateChangeLogs || [];

      return logs.filter(
        log => !!log.subtype.match(`date_change_${this.type}`.toUpperCase())
      );
    },
    dateChangeStatus() {
      if (this.dateChangeLogs.length > 0) {
        const status = this.dateChangeLogs[0].subtype.split(".")[1];

        switch (status) {
          case "APPROVE":
            return this.$options.statuses.approved;
          case "REJECT":
            return this.$options.statuses.rejected;
          case "WITHDRAW":
            return this.withdrawn
              ? this.$options.statuses.withdrawn
              : undefined;
          case "REQUEST":
            return this.$options.statuses.pending;
        }
      }
    },
    hoverText() {
      switch (this.dateChangeStatus) {
        case this.$options.statuses.rejected:
        case this.$options.statuses.approved:
        case this.$options.statuses.pending:
        case this.$options.statuses.withdrawn:
          try {
            const data = JSON.parse(this.dateChangeLogs[0].content);
            return Object.keys(data)
              .reduce((acc, key) => {
                let value = data[key];

                // Convert dates to local
                if (["original_date", "requested_date"].includes(key)) {
                  value = DateFormat.toLocalDateAndTime(value);
                }

                acc.push(`${startCase(key)}: ${value}`);

                return acc;
              }, [])
              .join(" | ");
          } catch (err) {
            // data is not a valid JSON
          }

        break;

        default: return 'No further information available.';


      }

      return "No further information available.";
    },
    canRequestDateChange() {
      return this.type === "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_request_date_change_start"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_request_date_change_finish"
          );
    },
    canRequestDateChangeByUser() {
      return this.type === "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_request_date_change_start_by_user"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_request_date_change_finish_by_user"
          );
    },
    canApproveDateChange() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_approve_date_change_start"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_approve_date_change_finish"
          );
    },
    canApproveDateChangeByUser() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_approve_date_change_start_by_user"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_approve_date_change_finish_by_user"
          );
    },
    canRejectDateChange() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_reject_date_change_start"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_reject_date_change_finish"
          );
    },
    canRejectDateChangeByUser() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_reject_date_change_start_by_user"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_reject_date_change_finish_by_user"
          );
    },
    canWithdrawDateChange() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_withdraw_date_change_start"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_withdraw_date_change_finish"
          );
    },
    canWithdrawDateChangeByUser() {
      return this.type == "start"
        ? this.$store.getters["OrderState/getStateByKey"](
            "can_withdraw_date_change_start_by_user"
          )
        : this.$store.getters["OrderState/getStateByKey"](
            "can_withdraw_date_change_finish_by_user"
          );
    }
  },
  methods: {
    async approveDateChange() {
      this.approving = true;

      try {
        const order = await this.$options.orderService
          .approveDateChange(this.order.id, {
            startOrFinish: this.type,
            includes: "dateChangeLogs,state"
          })
          .then(response => JsonDenormalizer.denormalize(response.data));

        this.$set(this.order, "requiredStart", order.requiredStart);
        this.$set(this.order, "requiredFinish", order.requiredFinish);
        this.$set(this.order, "requestedStart", order.requestedStart);
        this.$set(this.order, "requestedFinish", order.requestedFinish);
        this.$set(this.order, "dateChangeLogs", order.dateChangeLogs);

        // Set order state
        this.$store.dispatch("OrderState/fetchState", this.order.id);

        this.$buefy.toast.open({
          message: "Date change request has been approved.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}

      this.approving = false;
    },
    async withdrawDateChange() {
      this.withdrawing = true;

      try {
        const order = await this.$options.orderService
          .withdrawDateChange(this.order.id, {
            startOrFinish: this.type,
            includes: "dateChangeLogs,state"
          })
          .then(response => JsonDenormalizer.denormalize(response.data));

        this.$set(this.order, "requestedStart", order.requestedStart);
        this.$set(this.order, "requestedFinish", order.requestedFinish);
        this.$set(this.order, "dateChangeLogs", order.dateChangeLogs);

        // Set order state
        this.$store.dispatch("OrderState/fetchState", this.order.id);

        this.withdrawn = true;

        this.$buefy.toast.open({
          message: "Date change request has been withdrawn.",
          type: "is-info",
          position: "is-top-right"
        });
      } catch (err) {}

      this.withdrawing = false;
    },
    onDateChangeModalSubmit(order) {
      this.$set(this.order, "requiredStart", order.requiredStart);
      this.$set(this.order, "requiredFinish", order.requiredFinish);
      this.$set(this.order, "requestedStart", order.requestedStart);
      this.$set(this.order, "requestedFinish", order.requestedFinish);
      this.$set(
        this.order,
        "dateChangeRequestStart",
        order.dateChangeRequestStart
      );
      this.$set(
        this.order,
        "dateChangeRequestFinish",
        order.dateChangeRequestFinish
      );
      this.$set(this.order, "dateChangeLogs", order.dateChangeLogs);

      // Set order state
      this.$store.dispatch("OrderState/fetchState", this.order.id);
    },
    onDateChangeRejectModalSubmit(order) {
      this.$set(this.order, "requestedStart", order.requestedStart);
      this.$set(this.order, "requestedFinish", order.requestedFinish);
      this.$set(this.order, "dateChangeLogs", order.dateChangeLogs);

      // Set order state
      this.$store.dispatch("OrderState/fetchState", this.order.id);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/OrderKpi/OrderKpiDateChange.vue